import React, { useEffect } from 'react'
import BaseRadio from '../Form/BaseRadio'
import { Control, Controller, ControllerFieldState, FieldValues, Path } from 'react-hook-form'
import { RegisterOptions } from 'react-hook-form/dist/types/validator'
import { RadioGroupProps } from '@chakra-ui/react'
import { SelectItem } from '../Form/BaseSelect'
import { ControllerRenderProps } from 'react-hook-form/dist/types/controller'
import {extractErrorStatus} from "../../helpers/forms";

type Props<T extends FieldValues> = {
  name: Path<T>
  label?: string
  placeholder?: string
  helperText?: string
  stackDirection?: 'row' | 'column'
  setDefaultValue?: boolean
  control: Control<T>
  rules?: Omit<
    RegisterOptions<any, any>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >
  setValueAs?: (value: string) => string | number
  options: SelectItem[]
} & Omit<RadioGroupProps, 'children'>

const FormRadio = <T extends FieldValues>({
  name,
  placeholder,
  helperText,
  stackDirection = 'row',
  setDefaultValue = true,
  label,
  control,
  rules,
  setValueAs,
  options,
  ...props
}: Props<T>) => {
  return (
    <Controller
      control={control}
      rules={rules}
      name={name}
      render={({ field, fieldState }) => {
        return (
          <RadioContent
            {...props}
            field={field}
            fieldState={fieldState}
            label={label}
            name={name}
            placeholder={placeholder}
            options={options}
            rules={rules}
            stackDirection={stackDirection}
            helperText={helperText}
            setDefaultValue={setDefaultValue}
          />
        )
      }}
    />
  )
}

type RadioContentProps<T extends FieldValues> = {
  field: ControllerRenderProps<T>
  fieldState: ControllerFieldState,
  stackDirection?: 'row' | 'column',
  helperText?: string,
  setDefaultValue: boolean
} & Pick<Props<T>, 'name' | 'placeholder' | 'options' | 'label' | 'rules'> &
  Omit<RadioGroupProps, 'children'>

const RadioContent = <T extends FieldValues>(props: RadioContentProps<T>) => {
  const { field, fieldState, options, placeholder, name, label, stackDirection, rules, helperText,setDefaultValue, ...rest } = props
  const [errorMessage, isInvalid] = extractErrorStatus(fieldState)

  useEffect(() => {
    if (!field.value && options.length && setDefaultValue) {
      // set default value for radio button if none given or enforced to set
      field.onChange(options[0].value)
    }
  }, [field, field.value, options])

  return (
    <BaseRadio
      {...rest}
      placeholder={placeholder}
      label={label}
      options={options}
      stackDirection={stackDirection}
      helperText={errorMessage || helperText}
      isRequired={!!rules?.required}
      isInvalid={isInvalid}
      {...field}
      value={(field.value ?? '') + ''}
    />
  )
}

export default FormRadio
